import { type ComponentPropsWithoutRef, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

/**
 * Markdown renderer for ASSISTANT chat content.
 *
 * The model replies in GitHub-flavoured markdown (bold, lists, tables, and
 * backtick-quoted `item_id`s), so rendering the raw string shows the syntax
 * literally. This wraps `react-markdown` + `remark-gfm` with a compact
 * component map tuned to sit inside a chat bubble.
 *
 * Raw HTML is intentionally NOT enabled (no `rehype-raw`): react-markdown's
 * default escapes embedded HTML, so untrusted model output can't inject markup.
 * `react-markdown` also tolerates partial/incomplete markdown, so the same
 * renderer is safe to use on the streaming partial-text bubble.
 */
interface MarkdownProps {
  text: string;
}

function CodeBlock({ className, children, ...props }: ComponentPropsWithoutRef<"code">) {
  const isInline = !className?.includes("language-");
  if (isInline) {
    return (
      <code
        className="border-border/60 bg-background/70 text-foreground rounded border px-1 py-0.5 font-mono text-[0.85em]"
        {...props}
      >
        {children}
      </code>
    );
  }
  return (
    <code className={cn("font-mono", className)} {...props}>
      {children}
    </code>
  );
}

/**
 * Compact renderer set. Spacing collapses on the first/last child so the bubble
 * doesn't gain stray top/bottom padding around a single paragraph.
 */
const components: Components = {
  p: ({ className, ...props }) => (
    <p className={cn("mb-2 leading-6 last:mb-0", className)} {...props} />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn("mb-2 ml-1 list-outside list-disc space-y-1 pl-4 last:mb-0", className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn("mb-2 ml-1 list-outside list-decimal space-y-1 pl-4 last:mb-0", className)}
      {...props}
    />
  ),
  li: ({ className, ...props }) => <li className={cn("leading-6", className)} {...props} />,
  h1: ({ className, ...props }) => (
    <h1 className={cn("mt-3 mb-2 text-base font-semibold first:mt-0", className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2 className={cn("mt-3 mb-2 text-sm font-semibold first:mt-0", className)} {...props} />
  ),
  h3: ({ className, ...props }) => (
    <h3 className={cn("mt-2 mb-1.5 text-sm font-semibold first:mt-0", className)} {...props} />
  ),
  strong: ({ className, ...props }) => (
    <strong className={cn("font-semibold", className)} {...props} />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn("text-primary font-medium underline underline-offset-2", className)}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn("border-border text-muted my-2 border-l-2 pl-3 italic", className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("border-border my-3", className)} {...props} />
  ),
  code: CodeBlock,
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "border-border/60 bg-background/70 my-2 overflow-x-auto rounded-md border p-2.5 text-[0.8em] leading-relaxed",
        className,
      )}
      {...props}
    />
  ),
  table: ({ className, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table
        className={cn("border-border w-full border-collapse rounded border text-xs", className)}
        {...props}
      />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "border-border bg-background/60 border px-2 py-1 text-left font-semibold",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td className={cn("border-border border px-2 py-1 align-top", className)} {...props} />
  ),
};

function MarkdownImpl({ text }: MarkdownProps) {
  return (
    <div className="break-words">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

export const Markdown = memo(MarkdownImpl);
